// One journal card's body (spec 14 §4.4, D-512 ruling 1, D-516 ruling 4): the
// tool's template, the agent's note in the untrusted frame, the run's trigger,
// status, outcome and error inline (14-A has no run inspector), and the
// verdict's evidence. Money appears only where the server served it (§7.5).

import { useTranslation } from "react-i18next";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { UntrustedText } from "../../../../components/untrusted-text";
import { formatMinor } from "../../inventory/lib/cost";
import {
  countdown,
  countdownUrgent,
  evidenceOf,
  impactTone,
  type AgentAction,
} from "../lib/agents-lib";
import { outcomeText, problemText, toolName } from "../lib/labels";
import { Template } from "./card-templates";
import { RedraftOffer } from "./draft-offer";

const TONE_VARIANT: Record<ReturnType<typeof impactTone>, ChipVariant> = {
  bad: "bad",
  warn: "warn",
  neutral: "neutral",
};

/** The impact chip: money red-scaled against the verdict's cap, or a count. */
export function ImpactChip({ action }: { action: AgentAction }) {
  const { t, i18n } = useTranslation();
  if (action.impact_minor !== undefined && action.impact_currency !== undefined) {
    return (
      <StatusChip
        variant={TONE_VARIANT[impactTone(action)]}
        dot={false}
        data-testid="action-impact"
      >
        {formatMinor(action.impact_minor, action.impact_currency, i18n.language)}
      </StatusChip>
    );
  }
  if (action.impact_count !== null && action.impact_count > 0) {
    const key =
      action.impact_dimension === "schedule"
        ? "agents.journal.impact.schedule"
        : action.impact_dimension === "message"
          ? "agents.journal.impact.message"
          : "agents.journal.impact.count";
    return (
      <StatusChip variant="neutral" dot={false} data-testid="action-impact">
        {t(key, { count: action.impact_count })}
      </StatusChip>
    );
  }
  return null;
}

const REVERSIBILITY_VARIANT: Record<AgentAction["reversibility"], ChipVariant> = {
  reversible: "ok",
  compensatable: "neutral",
  irreversible: "warn",
};

export function ReversibilityBadge({ action }: { action: AgentAction }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={REVERSIBILITY_VARIANT[action.reversibility]}
      dot={false}
      data-testid="action-reversibility"
    >
      {t(`agents.detail.policies.reversibility.${action.reversibility}`)}
    </StatusChip>
  );
}

/** Time left on a proposed card, prominent when under an hour. */
export function Countdown({ action, now }: { action: AgentAction; now: number }) {
  const { t } = useTranslation();
  if (action.status !== "proposed") return null;
  const left = countdown(action.expires_at, now);
  const text = left.expired
    ? t("agents.journal.countdown.expired")
    : left.days > 0
      ? t("agents.journal.countdown.days", { days: left.days, hours: left.hours })
      : left.hours > 0
        ? t("agents.journal.countdown.hours", { hours: left.hours, minutes: left.minutes })
        : t("agents.journal.countdown.minutes", { minutes: left.minutes });
  return (
    <span
      className="ag-countdown"
      data-urgent={countdownUrgent(left)}
      data-testid="action-countdown"
    >
      {text}
    </span>
  );
}

const STATUS_VARIANT: Record<AgentAction["status"], ChipVariant> = {
  proposed: "accent",
  approved: "accent",
  executing: "accent",
  executed: "ok",
  rejected: "neutral",
  failed: "bad",
  undone: "neutral",
  expired: "neutral",
};

export function ActionStatusChip({ action }: { action: AgentAction }) {
  const { t } = useTranslation();
  return (
    <StatusChip variant={STATUS_VARIANT[action.status]} data-testid="action-status">
      {t(`agents.journal.status.${action.status}`)}
    </StatusChip>
  );
}

const EVIDENCE_KEYS = [
  "impact_count",
  "shortage_count",
  "qty_planned",
  "items",
  "max_autonomous_items",
  "cap",
  "used",
  "remaining",
] as const;

/** The verdict's evidence the dictionary names; money stays on the impact chip. */
function Evidence({ action }: { action: AgentAction }) {
  const { t, i18n } = useTranslation();
  const evidence = evidenceOf(action);
  const rows = EVIDENCE_KEYS.flatMap((key) => {
    const value = evidence[key];
    if (typeof value === "number") return [[key, value.toLocaleString(i18n.language)] as const];
    if (typeof value === "string" && value.length > 0) return [[key, value] as const];
    return [];
  });
  return (
    <details data-testid="action-evidence">
      <summary>{t("agents.journal.evidence")}</summary>
      <dl className="ag-detail-list">
        <dt>{t("agents.journal.params")}</dt>
        <dd>{toolName(t, action.tool_code)}</dd>
        <dt>{t("agents.journal.columns.status")}</dt>
        <dd>{t(`agents.journal.applied.${action.autonomy_applied}`)}</dd>
        {rows.map(([key, value]) => (
          <div key={key} style={{ display: "contents" }}>
            <dt>{t(`agents.journal.evidenceKey.${key}`)}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/** The run line: trigger, status, outcome and error inline (D-512 ruling 1). */
function RunLine({ action }: { action: AgentAction }) {
  const { t } = useTranslation();
  if (action.run === null) return null;
  const outcome = outcomeText(t, action.run.outcome);
  return (
    <p className="ag-hint" data-testid="action-run">
      {t("agents.journal.run.label")}:{" "}
      {t("agents.journal.run.line", {
        trigger: t(`agents.trigger.${action.run.trigger_type}`),
        status: t(`agents.runStatus.${action.run.status}`),
      })}
      {outcome !== undefined ? ` · ${outcome}` : ""}
      {action.run.error !== null
        ? ` · ${t("agents.journal.run.error", { error: problemText(t, action.run.error.code) })}`
        : ""}
    </p>
  );
}

export function ActionBody({ action }: { action: AgentAction }) {
  const { t } = useTranslation();
  return (
    <div className="ag-card-body" data-testid="action-body">
      <div className="ag-row">
        <ReversibilityBadge action={action} />
        <ImpactChip action={action} />
      </div>
      {action.verdict_reason === "tool_orphaned" ? (
        // §9 item 4: a proposal its tool's removal expired says so.
        <p className="ag-hint" data-testid="action-tool-removed">
          {t("agents.journal.toolRemoved")}
        </p>
      ) : null}
      <Template action={action} />
      {action.agent_note !== null && action.agent_note.length > 0 ? (
        <UntrustedText
          label={t("agents.journal.note.label")}
          frameNote={t("agents.journal.note.frame")}
          text={action.agent_note}
          lang={action.agent_note_locale ?? undefined}
          data-testid="action-note"
        />
      ) : null}
      {action.error !== null ? (
        <p className="ag-error" data-testid="action-error">
          {problemText(t, action.error.code)}
        </p>
      ) : null}
      <RunLine action={action} />
      <RedraftOffer action={action} />
      <Evidence action={action} />
    </div>
  );
}
